/**
 * Virtual Office Q&A cluster (ที่ตั้งสำนักงานเพื่อจดทะเบียนบริษัทและจดทะเบียนภาษีมูลค่าเพิ่ม).
 *
 * Hand-authored core answers merged with the shared deterministic legal-cluster
 * expander. No AI call at build or request time.
 *
 * Content rules: no service prices, no promise that any agency will approve a
 * filing, no unverifiable statistics; every answer ends with the reviewed-as-of
 * and verify-with-the-agency tail.
 */
import { LEGAL_QA_TARGET_CORE, buildLegalClusterQA, type WaveQA } from "@/data/_qa/legal-clusters";
import { VO_TOPIC_AXIS, VO_ZONE_AXIS } from "./axes";

export type VoQA = WaveQA;

export const VO_REVIEWED_TH = "กันยายน 2569";
export const VO_REVIEWED_ISO = "2026-09-15";

const NO_PRICE = "เว็บไซต์ไม่แสดงค่าบริการ ขอใบเสนอราคาเฉพาะเรื่องได้ทางโทรศัพท์ LINE หรืออีเมล";
const VOLATILE =
  "หลักเกณฑ์การจดทะเบียนนิติบุคคล การจดทะเบียนภาษีมูลค่าเพิ่ม และเอกสารที่เจ้าหน้าที่เรียกเพิ่มเปลี่ยนแปลงได้ ควรยืนยันกับกรมพัฒนาธุรกิจการค้าและสำนักงานสรรพากรพื้นที่ที่สถานประกอบการตั้งอยู่ก่อนดำเนินการ";
const ASOF = `ข้อมูลทบทวน ณ ${VO_REVIEWED_TH} ${VOLATILE} ${NO_PRICE}`;

const TOPIC = {
  address: { topicSlug: "registered-address", topicTh: "ที่ตั้งสำนักงานเพื่อจดทะเบียนบริษัท" },
  vat: { topicSlug: "vat-address", topicTh: "ที่ตั้งสถานประกอบการเพื่อจดทะเบียนภาษีมูลค่าเพิ่ม" },
  inspect: { topicSlug: "site-inspection", topicTh: "การตรวจสภาพกิจการของเจ้าหน้าที่ ณ สถานประกอบการ" },
  mail: { topicSlug: "mail-handling", topicTh: "การรับจดหมายและหนังสือราชการที่ที่ตั้งสำนักงาน" },
  contract: { topicSlug: "contract-terms", topicTh: "สัญญาใช้บริการที่ตั้งสำนักงานและข้อที่ต้องตกลงให้ชัด" },
} as const;

/** Hand-authored questions that lead the hub. */
const CORE: VoQA[] = [
  {
    slug: "vo-what-is-virtual-office",
    q: "Virtual Office คืออะไร ใช้จดทะเบียนบริษัทได้จริงหรือไม่",
    a: `Virtual Office คือการใช้ที่ตั้งสำนักงานที่มีผู้ให้บริการดูแลสถานที่ รับเอกสาร และให้สิทธิผู้ใช้บริการนำที่อยู่นั้นไปใช้เป็นที่ตั้งสำนักงานของนิติบุคคล ใช้จดทะเบียนบริษัทได้หากสถานที่นั้นมีอยู่จริง ระบุที่อยู่ได้ครบถึงเลขห้องและชั้น และเจ้าของหรือผู้มีสิทธิในสถานที่ให้ความยินยอมเป็นลายลักษณ์อักษรพร้อมหลักฐานสิทธิ สิ่งที่ต่างจากการเช่าพื้นที่ทำงานคือไม่จำเป็นต้องมีโต๊ะทำงานประจำ แต่ยังต้องมีผู้รับการติดต่อจากหน่วยงานได้ในเวลาทำการ ${ASOF}`,
    synonyms: ["virtual office คือ", "ที่อยู่จดบริษัท", "เช่าที่อยู่จดทะเบียนบริษัท"],
    ...TOPIC.address,
  },
  {
    slug: "vo-can-register-vat-at-virtual-office",
    q: "ใช้ Virtual Office จดทะเบียนภาษีมูลค่าเพิ่มได้หรือไม่",
    a: `ได้ ถ้าสถานที่นั้นรองรับเงื่อนไขที่กรมสรรพากรใช้พิจารณา คือมีหลักฐานสิทธิใช้สถานที่ ติดป้ายแสดงชื่อผู้ประกอบการให้เห็นจากภายนอก มีพื้นที่ใช้งานที่ระบุได้ และมีผู้รับการตรวจสภาพกิจการในเวลาทำการ ผู้ให้บริการบางรายรับเฉพาะการจดทะเบียนบริษัทแต่ไม่รับให้จดทะเบียนภาษีมูลค่าเพิ่ม จึงควรถามให้ชัดและระบุในสัญญาก่อนชำระเงิน ผลการพิจารณาเป็นอำนาจของเจ้าหน้าที่ผู้ตรวจ ไม่มีผู้ให้บริการรายใดรับประกันผลได้ ${ASOF}`,
    synonyms: ["virtual office จด vat", "จดภาษีมูลค่าเพิ่มที่อยู่เช่า", "ภ.พ.01 virtual office"],
    ...TOPIC.vat,
  },
  {
    slug: "vo-vat-inspection-what-happens",
    q: "เจ้าหน้าที่ตรวจสภาพกิจการดูอะไรบ้าง",
    a: `โดยทั่วไปเจ้าหน้าที่จะดูว่าที่อยู่ตามคำขอตรงกับสถานที่จริง มีป้ายแสดงชื่อผู้ประกอบการที่มองเห็นจากภายนอก มีพื้นที่ที่ใช้ประกอบกิจการหรือเก็บเอกสาร มีผู้ให้ข้อมูลเกี่ยวกับกิจการอยู่ที่นั้น และอาจถ่ายภาพประกอบรายงาน ควรเตรียมหนังสือรับรองนิติบุคคล สัญญาเช่าหรือหนังสือยินยอมให้ใช้สถานที่ และเอกสารแสดงลักษณะธุรกิจไว้ที่สถานที่ หากไม่มีผู้รับการตรวจในวันที่เจ้าหน้าที่เข้ามา เรื่องจะถูกเลื่อนหรือถูกขอเอกสารเพิ่ม ${ASOF}`,
    synonyms: ["สรรพากรมาตรวจที่อยู่", "ตรวจสภาพกิจการ vat", "เจ้าหน้าที่ตรวจสถานประกอบการ"],
    ...TOPIC.inspect,
  },
  {
    slug: "vo-consent-letter-requirements",
    q: "หนังสือยินยอมให้ใช้สถานที่ต้องมีรายละเอียดอะไร",
    a: `ควรมีชื่อและเลขประจำตัวของผู้ให้ความยินยอม ความสัมพันธ์กับสถานที่ว่าเป็นเจ้าของกรรมสิทธิ์หรือผู้มีสิทธิตามสัญญาเช่า ที่อยู่ของสถานที่ที่ระบุครบถึงเลขห้องและชั้น ข้อความยินยอมให้ใช้เป็นที่ตั้งสำนักงานและสถานประกอบการของนิติบุคคลที่ระบุชื่อไว้ วันที่ ลายมือชื่อ และแนบสำเนาหลักฐานสิทธิ เช่น โฉนด หนังสือกรรมสิทธิ์ห้องชุด หรือสัญญาเช่า พร้อมสำเนาบัตรประจำตัวของผู้ให้ความยินยอมที่รับรองสำเนาถูกต้อง ${ASOF}`,
    synonyms: ["หนังสือยินยอมให้ใช้สถานที่", "หนังสือยินยอมให้ใช้ที่อยู่จดบริษัท"],
    ...TOPIC.address,
  },
  {
    slug: "vo-why-vat-application-rejected",
    q: "ทำไมคำขอจดทะเบียนภาษีมูลค่าเพิ่มจึงถูกให้แก้ไขหรือไม่ผ่าน",
    a: `สาเหตุที่พบบ่อยคือที่อยู่ในคำขอไม่ตรงกับหนังสือรับรองหรือสัญญาแม้เพียงเลขห้อง ไม่มีป้ายแสดงชื่อผู้ประกอบการ ไม่มีผู้รับการตรวจในวันที่เจ้าหน้าที่เข้าตรวจ หลักฐานสิทธิใช้สถานที่ไม่ครบหรือผู้ให้ความยินยอมไม่ใช่ผู้มีสิทธิจริง ลักษณะธุรกิจตามวัตถุที่ประสงค์ไม่สอดคล้องกับพื้นที่ที่ใช้ และเอกสารประกอบไม่มีการรับรองสำเนา แนวทางแก้คือตรวจรูปแบบที่อยู่ให้เหมือนกันทุกฉบับ จัดสถานที่ให้พร้อมรับการตรวจ และยื่นเอกสารครบในรอบเดียว ${ASOF}`,
    synonyms: ["จด vat ไม่ผ่าน", "ภ.พ.01 ถูกตีกลับ", "สรรพากรให้แก้ไขเอกสาร"],
    ...TOPIC.vat,
  },
  {
    slug: "vo-signage-requirement",
    q: "ต้องติดป้ายชื่อบริษัทที่สถานประกอบการหรือไม่",
    a: `ควรติด เพราะป้ายแสดงชื่อผู้ประกอบการเป็นสิ่งที่เจ้าหน้าที่ตรวจเป็นลำดับแรกเมื่อเข้าตรวจสภาพกิจการ และช่วยยืนยันว่ามีการใช้สถานที่จริง ป้ายควรแสดงชื่อนิติบุคคลตามที่จดทะเบียน ติดในตำแหน่งที่มองเห็นจากภายนอกบริเวณทางเข้าพื้นที่ที่ใช้ และควรถ่ายภาพเก็บไว้เป็นหลักฐานประกอบคำขอ หากอาคารมีข้อจำกัดเรื่องการติดป้าย ควรขอหนังสือหรือแนวทางจากผู้ดูแลอาคารและแจ้งเจ้าหน้าที่ตั้งแต่ยื่นคำขอ ${ASOF}`,
    synonyms: ["ติดป้ายชื่อบริษัท vat", "ป้ายบริษัทที่ virtual office"],
    ...TOPIC.inspect,
  },
  {
    slug: "vo-condo-as-registered-office",
    q: "ใช้คอนโดหรือบ้านพักเป็นที่ตั้งสำนักงานได้หรือไม่",
    a: `ทำได้ในทางทะเบียนหากเจ้าของสถานที่ยินยอมและมีหลักฐานสิทธิ แต่ต้องตรวจข้อบังคับของนิติบุคคลอาคารชุดหรือโครงการก่อน เพราะหลายแห่งกำหนดให้ใช้เพื่อการอยู่อาศัยเท่านั้นและห้ามประกอบธุรกิจหรือห้ามติดป้าย หากข้อบังคับห้าม การนำไปใช้อาจเกิดข้อพิพาทกับนิติบุคคลอาคารชุด และการจดทะเบียนภาษีมูลค่าเพิ่มจะยากขึ้นเพราะติดป้ายและรับการตรวจไม่ได้ ควรขอหนังสือยืนยันจากผู้ดูแลอาคารไว้ล่วงหน้า ${ASOF}`,
    synonyms: ["จดบริษัทที่คอนโด", "ใช้บ้านจดบริษัท", "คอนโดจด vat ได้ไหม"],
    topicSlug: "condo-rules",
    topicTh: "การใช้ห้องชุดหรือที่พักอาศัยเป็นที่ตั้งสำนักงาน",
  },
  {
    slug: "vo-address-change-notification",
    q: "ย้ายที่ตั้งสำนักงานต้องแจ้งใครและภายในเมื่อไร",
    a: `ต้องยื่นจดทะเบียนแก้ไขที่ตั้งสำนักงานต่อกรมพัฒนาธุรกิจการค้า และแจ้งเปลี่ยนแปลงทะเบียนภาษีมูลค่าเพิ่มต่อกรมสรรพากรภายในกำหนดเวลาที่หน่วยงานกำหนด นอกจากนี้ควรแจ้งธนาคาร สำนักงานประกันสังคม คู่สัญญา และปรับที่อยู่ในใบกำกับภาษีให้ตรงกับทะเบียนใหม่ หากไม่แจ้ง หนังสือราชการจะถูกส่งไปที่อยู่เดิมและอาจทำให้พลาดกำหนดเวลาตอบ ควรตรวจกำหนดเวลาที่ใช้อยู่จริงกับแต่ละหน่วยงานก่อนย้าย ${ASOF}`,
    synonyms: ["ย้ายที่ตั้งบริษัท", "แจ้งเปลี่ยนที่อยู่ vat", "เปลี่ยนที่ตั้งสำนักงานแห่งใหญ่"],
    topicSlug: "address-change",
    topicTh: "การย้ายที่ตั้งและการแจ้งเปลี่ยนแปลงข้อมูลนิติบุคคล",
  },
  {
    slug: "vo-tax-invoice-address",
    q: "ที่อยู่ในใบกำกับภาษีต้องเป็นที่อยู่เดียวกับที่จดทะเบียนไว้หรือไม่",
    a: `ใบกำกับภาษีต้องแสดงที่อยู่ของสถานประกอบการตามที่จดทะเบียนไว้ และระบุว่าเป็นสำนักงานใหญ่หรือสาขาใดตามที่จดไว้ หากออกใบกำกับภาษีด้วยที่อยู่ที่ไม่ตรงกับทะเบียน ผู้ซื้ออาจมีปัญหาในการใช้ภาษีซื้อและเกิดข้อโต้แย้งภายหลัง เมื่อย้ายที่ตั้งจึงต้องแก้แบบฟอร์ม ระบบออกใบกำกับภาษี และเอกสารทางการค้าให้ตรงกันทันทีหลังการแจ้งเปลี่ยนแปลงมีผล ${ASOF}`,
    synonyms: ["ที่อยู่ในใบกำกับภาษี", "ใบกำกับภาษีสำนักงานใหญ่ สาขา"],
    topicSlug: "tax-invoice",
    topicTh: "การออกใบกำกับภาษีให้ตรงกับที่อยู่ที่จดทะเบียนไว้",
  },
  {
    slug: "vo-mail-from-court-or-revenue",
    q: "ถ้าหนังสือจากศาลหรือกรมสรรพากรส่งมาที่ที่ตั้งสำนักงาน จะรู้ได้อย่างไร",
    a: `ต้องตกลงวิธีแจ้งเตือนกับผู้ให้บริการไว้ล่วงหน้า โดยระบุว่าหนังสือจากศาล กรมสรรพากร หรือหน่วยงานที่มีกำหนดเวลาตอบ ต้องแจ้งผู้ติดต่อภายในกี่ชั่วโมงทำการ พร้อมสแกนส่งต่อและบันทึกวันที่รับไว้ เพราะกำหนดเวลาตามกฎหมายเริ่มนับจากการได้รับหนังสือ ไม่ใช่จากวันที่ท่านเปิดอ่าน ควรกำหนดผู้ติดต่อสำรองไว้ด้วยกรณีผู้ติดต่อหลักติดต่อไม่ได้ ${ASOF}`,
    synonyms: ["รับหมายศาลที่ virtual office", "จดหมายราชการส่งมาที่อยู่จดทะเบียน"],
    ...TOPIC.mail,
  },
  {
    slug: "vo-contract-points-to-agree",
    q: "สัญญาใช้บริการที่ตั้งสำนักงานควรตกลงเรื่องอะไรให้ชัด",
    a: `ควรระบุอย่างน้อยหกเรื่อง หนึ่ง สิทธิใช้ที่อยู่เพื่อจดทะเบียนบริษัทและจดทะเบียนภาษีมูลค่าเพิ่ม สอง หน้าที่ออกหนังสือยินยอมให้ใช้สถานที่และเอกสารสิทธิประกอบ สาม การให้ติดป้ายชื่อและการอยู่รับการตรวจสภาพกิจการ สี่ วิธีและกำหนดเวลาแจ้งเอกสารที่มีกำหนดเวลาตอบ ห้า สิ่งที่เกิดขึ้นเมื่อสัญญาสิ้นสุด รวมถึงกำหนดเวลาที่ต้องย้ายที่ตั้ง หก การรักษาความลับของเอกสาร ควรให้ตรวจร่างสัญญาก่อนลงนาม ${ASOF}`,
    synonyms: ["สัญญา virtual office", "เงื่อนไขเช่าที่อยู่จดบริษัท"],
    ...TOPIC.contract,
  },
  {
    slug: "vo-bank-account-opening",
    q: "เปิดบัญชีธนาคารของบริษัทโดยใช้ที่อยู่แบบนี้ได้หรือไม่",
    a: `ได้ แต่ธนาคารแต่ละแห่งมีเกณฑ์ตรวจลูกค้าของตนเอง โดยทั่วไปจะขอหนังสือรับรองนิติบุคคล วัตถุที่ประสงค์ รายชื่อผู้ถือหุ้น บัตรประจำตัวกรรมการ เอกสารแสดงสิทธิใช้สถานที่ และคำอธิบายลักษณะธุรกิจกับที่มาของรายได้ บางแห่งขอภาพถ่ายสถานประกอบการหรือขอเข้าเยี่ยมสถานที่ ควรสอบถามรายการเอกสารกับสาขาที่จะเปิดบัญชีก่อน และเตรียมเอกสารชุดเดียวกับที่ใช้ยื่นจดทะเบียนภาษีมูลค่าเพิ่มไว้ให้พร้อม ${ASOF}`,
    synonyms: ["เปิดบัญชีบริษัท virtual office", "ธนาคารขอเอกสารที่ตั้งสำนักงาน"],
    topicSlug: "bank-account",
    topicTh: "การเปิดบัญชีธนาคารของนิติบุคคลโดยใช้ที่อยู่จดทะเบียน",
  },
  {
    slug: "vo-work-permit-link",
    q: "ที่ตั้งสำนักงานมีผลต่อการขอใบอนุญาตทำงานของชาวต่างชาติหรือไม่",
    a: `มีผล เพราะการขอใบอนุญาตทำงานพิจารณาจากนายจ้างที่มีสถานประกอบการจริง เอกสารของนิติบุคคลที่ครบถ้วน และลักษณะงานที่สอดคล้องกับธุรกิจที่จดทะเบียนไว้ เจ้าหน้าที่อาจขอภาพถ่ายสถานที่ ผังการใช้พื้นที่ หรือหลักฐานว่ามีพื้นที่ทำงานสำหรับตำแหน่งที่ยื่น ที่อยู่ที่ใช้เพียงรับจดหมายโดยไม่มีพื้นที่ทำงานจึงมักต้องชี้แจงเพิ่ม ควรตรวจเงื่อนไขที่ใช้อยู่จริงกับสำนักงานจัดหางานในพื้นที่ก่อนวางแผน ${ASOF}`,
    synonyms: ["virtual office ขอ work permit", "ใบอนุญาตทำงานที่ตั้งสำนักงาน"],
    topicSlug: "work-permit-link",
    topicTh: "ความเชื่อมโยงระหว่างที่ตั้งสำนักงานกับใบอนุญาตทำงาน",
  },
  {
    slug: "vo-multiple-companies-same-address",
    q: "หลายบริษัทใช้ที่อยู่เดียวกันได้หรือไม่",
    a: `ในทางทะเบียนทำได้และพบได้ทั่วไปในอาคารสำนักงาน แต่เมื่อยื่นจดทะเบียนภาษีมูลค่าเพิ่ม เจ้าหน้าที่อาจขอหลักฐานเพิ่มเพื่อยืนยันว่าแต่ละกิจการประกอบธุรกิจจริง เช่น การแบ่งพื้นที่ใช้งาน ป้ายชื่อแยกของแต่ละราย และเอกสารการดำเนินงาน แนวทางที่ปลอดภัยคือให้ผู้ให้บริการระบุหมายเลขห้องหรือพื้นที่ที่ใช้ของแต่ละรายในหนังสือยินยอม และเตรียมเอกสารแสดงกิจกรรมทางธุรกิจไว้ที่สถานที่ ${ASOF}`,
    synonyms: ["หลายบริษัทที่อยู่เดียวกัน", "ที่อยู่ซ้ำกันจด vat"],
    topicSlug: "multi-branch",
    topicTh: "การจดสถานประกอบการหลายแห่งและสำนักงานสาขา",
  },
  {
    slug: "vo-online-business-registration",
    q: "ขายของออนไลน์ต้องจดทะเบียนอะไรและใช้ที่อยู่แบบนี้ได้หรือไม่",
    a: `ผู้ประกอบพาณิชยกิจที่ขายสินค้าหรือบริการผ่านช่องทางอิเล็กทรอนิกส์มีหน้าที่จดทะเบียนพาณิชย์ตามกฎหมายว่าด้วยทะเบียนพาณิชย์ และหากรายรับถึงเกณฑ์ต้องจดทะเบียนภาษีมูลค่าเพิ่มด้วย ที่อยู่ที่ใช้จดต้องเป็นสถานที่ที่ตรวจสอบได้และมีหลักฐานสิทธิใช้ ควรตรวจกับสำนักงานเขตหรือองค์กรปกครองส่วนท้องถิ่นที่รับจดทะเบียนพาณิชย์ในพื้นที่ว่าต้องใช้เอกสารใดเพิ่ม เพราะแนวปฏิบัติของแต่ละพื้นที่ต่างกันได้ ${ASOF}`,
    synonyms: ["จดทะเบียนพาณิชย์อิเล็กทรอนิกส์", "ขายออนไลน์จดบริษัท"],
    topicSlug: "ecommerce",
    topicTh: "ธุรกิจออนไลน์และการจดทะเบียนพาณิชย์อิเล็กทรอนิกส์",
  },
  {
    slug: "vo-timeline-company-and-vat",
    q: "จดบริษัทแล้วจดภาษีมูลค่าเพิ่มต่อ ใช้เวลาประมาณเท่าไร",
    a: `ระยะเวลาขึ้นกับความครบของเอกสารและคิวของหน่วยงาน โดยลำดับงานคือจองชื่อนิติบุคคล จัดทำคำขอและหนังสือยินยอมให้ใช้สถานที่ ยื่นจดทะเบียนบริษัท รับหนังสือรับรอง จัดสถานที่และป้ายชื่อให้พร้อม แล้วยื่นคำขอจดทะเบียนภาษีมูลค่าเพิ่มและรอการตรวจสภาพกิจการ ปัจจัยที่ทำให้ช้าที่สุดมักไม่ใช่การพิจารณา แต่เป็นการกลับไปแก้เอกสารที่ที่อยู่ไม่ตรงกันหรือหลักฐานสิทธิไม่ครบ ควรสอบถามคิวและกำหนดเวลาจริงกับหน่วยงานในพื้นที่ ${ASOF}`,
    synonyms: ["จดบริษัทกี่วัน", "จด vat ใช้เวลานานไหม"],
    ...TOPIC.vat,
  },
  {
    slug: "vo-end-of-contract-address",
    q: "ถ้าเลิกใช้บริการที่ตั้งสำนักงาน ต้องทำอะไรกับที่อยู่ที่จดไว้",
    a: `ต้องย้ายที่ตั้งสำนักงานและสถานประกอบการไปยังที่อยู่ใหม่ แล้วยื่นจดทะเบียนแก้ไขต่อกรมพัฒนาธุรกิจการค้าและแจ้งเปลี่ยนแปลงต่อกรมสรรพากรก่อนสัญญาสิ้นสุด หากปล่อยให้ที่อยู่ที่จดไว้ไม่มีผู้รับเอกสาร หนังสือราชการที่มีกำหนดเวลาตอบจะไม่ถึงผู้มีอำนาจและอาจเสียสิทธิ ควรกำหนดในสัญญาว่าผู้ให้บริการจะส่งต่อเอกสารต่อเนื่องอีกระยะหนึ่งหลังสิ้นสุดสัญญาเพื่อลดความเสี่ยงช่วงเปลี่ยนผ่าน ${ASOF}`,
    synonyms: ["ยกเลิก virtual office", "สัญญาหมดต้องย้ายที่อยู่บริษัท"],
    ...TOPIC.contract,
  },
];

const generated = buildLegalClusterQA({
  prefix: "vo",
  clusterTh: "งานที่ตั้งสำนักงานเพื่อจดทะเบียนบริษัทและจดทะเบียนภาษีมูลค่าเพิ่ม",
  authorityTh: "กรมพัฒนาธุรกิจการค้าและสำนักงานสรรพากรพื้นที่ที่สถานประกอบการตั้งอยู่",
  reviewedTh: VO_REVIEWED_TH,
  volatileNote: VOLATILE,
  topics: VO_TOPIC_AXIS,
  extraTopics: VO_ZONE_AXIS,
  qaTarget: LEGAL_QA_TARGET_CORE,
});

const seen = new Set(CORE.map((q) => q.slug));

export const VO_QA: VoQA[] = [...CORE, ...generated.filter((q) => !seen.has(q.slug))];

export const VO_QA_BY_SLUG: Record<string, VoQA> = Object.fromEntries(VO_QA.map((q) => [q.slug, q]));

export const VO_QA_COUNT = VO_QA.length;
